import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { abbreviateDate, addTimeToDate, getRelativeDate } from '@utils/date';
import { formatTime, replaceTemplateString } from '@utils/strings';

import { TravellerJourneyAction } from 'api/types';
import { TravellerNodeActionCard } from 'ui/journey-builder/node-types/node-container';
import { useFlowNodes } from 'ui/traveller-journey-builder/hooks/use-nodes';
import { NodeActions } from 'ui/traveller-journey-builder/node-types/common';
import { NodeContainer } from 'ui/traveller-journey-builder/node-types/node-container';
import { TravellerJourneyNode } from 'ui/traveller-journey-builder/node-types/types';

import { newDateIgnoreLocalTimezone } from '../timezone';

type ActionType = 'wait_until_relative_date';

export const getDescription = (
  action: TravellerJourneyAction<ActionType>,
  availableValues: Record<string, string>
) => {
  const { t } = getScopedTranslate(nodeLocale);
  const referenceDate = action.values?.referenceDate?.value;

  const comparator = (action.values?.comparator?.value ?? '') as 'lt' | 'gt' | '';
  const variables = action.values?.referenceDate?.variables || {};
  const field = variables[Object.keys(variables)[0]]?.label ?? undefined;
  const days = action.values?.days?.value ?? '0';
  const time = formatTime(action.values?.hour?.value, action.values?.minute?.value);
  const defaultDescription =
    comparator === 'lt'
      ? t('triggersBefore', { days, field, time })
      : comparator === 'gt'
      ? t('triggersAfter', { days, field, time })
      : t('triggersOn', { field, time });

  if (referenceDate) {
    const referenceDateValue = replaceTemplateString(referenceDate, availableValues);
    if (referenceDateValue) {
      const relativeReferenceDate = getRelativeDate(referenceDateValue, comparator, Number(days));
      const dateWithTime = addTimeToDate(relativeReferenceDate, time);
      const date = newDateIgnoreLocalTimezone(dateWithTime);

      return t('triggersNext', {
        date: abbreviateDate(date, 'en', 'datetime'),
      });
    }
    return defaultDescription;
  } else if (action.savedValues?.date) {
    const date = newDateIgnoreLocalTimezone(action?.savedValues?.date);
    return t('triggersNext', { date: abbreviateDate(date, 'en', 'datetime') });
  }
  return defaultDescription;
};

export const Node = (node: TravellerJourneyNode<ActionType>) => {
  const { t } = useScopedTranslate(nodeLocale);
  useFlowNodes();

  return (
    <NodeContainer node={node}>
      <TravellerNodeActionCard
        node={node}
        label={t('title')}
        descriptionText={node.data.nodeDescription}
      >
        <NodeActions node={node} />
      </TravellerNodeActionCard>
    </NodeContainer>
  );
};

const nodeLocale = {
  en: {
    title: 'Wait until # date',
    noDescription: 'No description',
    triggersNext: 'Triggers next action on {date}',
    triggersBefore: 'Triggers next action {days} days before {field} at {time}',
    triggersOn: 'Triggers next action on {field} at {time}',
    triggersAfter: 'Triggers next action {days} days after {field} at {time}',
  },
};
